import { useNavigate } from 'react-router-dom';
import type { Listing } from '../../types';
import { useCan } from '../../hooks/useCan';
import Card, { CardHeader, CardBody } from '../ui/Card';
import PlatformBadges from './PlatformBadges';
import PlatformOfferLink from './PlatformOfferLink';

export default function ListingPlatformsCard({ listing }: { listing: Listing }) {
  const navigate = useNavigate();
  const canReview = useCan('mutate') && !listing.deleted_at && !listing.merged_listing_id;
  const linkedUrls = Object.entries(listing.platform_urls ?? {});
  const candidates = listing.duplicate_candidates ?? [];
  const sources = listing.merge_sources ?? [];
  const linkedRows = linkedUrls.flatMap<{
    key: string;
    platform: string;
    url: string;
    source?: NonNullable<Listing['merge_sources']>[number];
  }>(([platform, url]) => {
    const platformSources = sources.filter(source => source.platform === platform);
    return platformSources.length ? platformSources.map(source => ({
      key: `${platform}:${source.id}`,
      platform,
      url: source.resolved_url || source.listing_url || url,
      source,
    })) : [{ key: platform, platform, url, source: undefined }];
  });
  for (const source of sources) {
    if (!linkedRows.some(row => row.source?.id === source.id)) {
      linkedRows.push({
        key: `source:${source.id}`,
        platform: source.platform,
        url: source.resolved_url || source.listing_url,
        source,
      });
    }
  }

  if (linkedRows.length === 1 && !linkedRows[0].source && candidates.length === 0) {
    const { platform, url } = linkedRows[0];
    return (
      <Card>
        <CardBody className="flex flex-wrap items-center gap-3">
          <h3 className="text-lg font-medium">Plattformen</h3>
          <PlatformOfferLink url={url} provider={platform} />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Plattformen" />
      <CardBody>
        <div className="overflow-x-auto">
          {linkedRows.length > 0 || candidates.length > 0 ? (
            <table aria-label="Plattformangebote" className="w-full min-w-[36rem] text-left text-sm">
              <tbody className="divide-y divide-gray-200">
                {linkedRows.map(({ key, platform, url, source }) => (
                  <tr key={key}>
                    <td className="w-px whitespace-nowrap py-2 pr-3">
                      {url ? <PlatformOfferLink url={url} provider={platform} /> : <PlatformBadges fallback={platform} />}
                    </td>
                    <td className="w-full max-w-0 py-2 pr-3">
                      {source && <span className="block truncate" title={`${source.title} (#${source.id})`}>
                        {source.title} (#{source.id})
                      </span>}
                    </td>
                    <td />
                    <td className="w-px whitespace-nowrap py-2 text-right text-xs font-medium">
                      {source && <button type="button" className="btn btn-text hover:underline"
                        onClick={() => navigate(`/inserat/${source.id}`)}>
                        Ursprungsinserat öffnen
                      </button>}
                    </td>
                  </tr>
                ))}
                {candidates.flatMap(candidate => {
                  const urls = Object.entries(candidate.platform_urls);
                  const offers = urls.length ? urls : [[
                    candidate.platform,
                    candidate.resolved_url || candidate.listing_url,
                  ]];
                  return offers.map(([platform, url]) => (
                    <tr key={`${candidate.id}:${platform}`}>
                      <td className="w-px whitespace-nowrap py-2 pr-3">
                        {url ? <PlatformOfferLink url={url} provider={platform} /> : <PlatformBadges fallback={platform} />}
                      </td>
                      <td className="w-full max-w-0 py-2 pr-3">
                        <button type="button" className="btn btn-text block w-full truncate text-left hover:underline"
                          title={`${candidate.title} (#${candidate.id})`}
                          onClick={() => navigate(`/inserat/${candidate.id}`)}>
                          {candidate.title} (#{candidate.id})
                        </button>
                      </td>
                      <td className="w-px whitespace-nowrap py-2 pr-3 text-xs font-medium">
                        <span className="inline-block rounded bg-warning-50 px-2 py-1 text-warning-800">
                          Vermutetes Duplikat
                          {candidate.image_match_percentage != null && (
                            <span className="block text-[11px] font-normal"
                              title="Anteil der verglichenen Bilder dieses Inserats, die im anderen Inserat wiedergefunden wurden.">
                              {Math.round(candidate.image_match_percentage)}% Übereinstimmung
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="w-px whitespace-nowrap py-2 text-right text-xs font-medium">
                        {canReview && (
                          <button type="button" className="btn btn-text hover:underline"
                            onClick={() => navigate(`/inserate/merge?ids=${listing.id},${candidate.id}`)}>
                            Jetzt prüfen
                          </button>
                        )}
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-gray-500">Keine Plattform-Links vorhanden.</p>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
